/**
 * @file FundingTable.tsx
 * @description This file contains the FundingTable component which displays a table of funding data using Ant Design's Table component.
 * The table includes columns for announced date, amount, type, and investors.
 */

import React from 'react';
import { Table } from 'antd';
import { Funding, Investor } from 'models/Supplier';
import moment from 'moment';
import millify from 'millify';

const MILLIFYOPTIONS = { space: true, precision: 2, locales: 'en-US' };

/**
 * @interface FundingTableProps
 * @description Props for the FundingTable component.
 * @property {Funding[] | undefined} data - An array of funding data or undefined.
 */
interface FundingTableProps {
  data: Funding[] | undefined;
}

/**
 * Represents a funding table component in React.
 * @param {object} FundingTableProps - The props for the FundingTable component.
 * @param {Array} FundingTableProps.data - The array of funding data to display in the table.
 * @returns {ReactElement} React functional component rendering a funding table.
 */
const FundingTable: React.FC<FundingTableProps> = ({ data }) => {
  const totalFunding = data?.reduce((acc, curr) => {
    const amount = parseInt(curr.amount);
    return acc + (isNaN(amount) ? 0 : amount);
  }, 0);

  const columns = [
    {
      title: 'Announced Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => moment(date).format('MMM DD, YYYY'),
      width: '160px',
      defaultSortOrder: 'descend' as const,
      sorter: (a: Funding, b: Funding) => moment(a.date).unix() - moment(b.date).unix()
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: string) => (parseInt(amount) > 0 ? `$ ${millify(parseInt(amount), MILLIFYOPTIONS)}` : 'N/A'),
      sorter: (a: Funding, b: Funding) => {
        const aAmount = isNaN(parseInt(a.amount)) ? 0 : parseInt(a.amount);
        const bAmount = isNaN(parseInt(b.amount)) ? 0 : parseInt(b.amount);
        return aAmount - bAmount;
      }
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type'
    },
    {
      title: 'Investors',
      dataIndex: 'investor',
      key: 'investor',
      render: (investors: Investor[] | undefined, rowData: Funding) => (
        <div className="flex flex-wrap">
          {rowData.undisclosed == 'true' ? (
            <span>Undisclosed</span>
          ) : !investors || investors?.length === 0 ? (
            <span>N/A</span>
          ) : (
            investors?.map((investor, index) => (
              <React.Fragment key={investor.company_id}>
                <a
                  href={`https://${investor.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  {investor.name}
                </a>
                {index < investors.length - 1 && <span>,&nbsp;</span>}
              </React.Fragment>
            ))
          )}
        </div>
      )
    }
  ];

  if (!data || data.length === 0) {
    return <div>No funding data available</div>;
  }

  return (
    <>
      <h3 className="text-xl font-semibold">
        Funding ( {`$ ${millify(totalFunding ? totalFunding : 0, MILLIFYOPTIONS)}`} )
      </h3>
      <Table columns={columns} dataSource={data} rowKey="date" pagination={{ hideOnSinglePage: true }} />
    </>
  );
};

export default FundingTable;
